import type { DailyProgress } from '@/lib/scorecards/daily';
import type { MetricStatus } from '@/lib/scorecards/status';

/**
 * Today's number against its daily goal (ADR-048) — three shapes on purpose:
 *
 *   attainment → a ring that FILLS (orders shipped vs today's target)
 *   budget     → a ring that DRAINS (defects vs today's allowance) — a
 *                filling bar would read backwards, 150% of a defect goal is
 *                a bad day
 *   level      → no ring, just value vs goal (on-time %, headcount) —
 *                nothing accumulates over the day
 *
 * Colour is the shared metric status, so a day can never be green on the
 * grid and grey here.
 */

const STATUS_TEXT: Record<MetricStatus, string> = {
  green: 'text-kw-success-text',
  yellow: 'text-kw-warning-text',
  red: 'text-kw-danger-text',
};

function fmt(n: number): string {
  return Number.isInteger(n) ? String(n) : n.toFixed(1);
}

export function DailyProgressRing({ progress, size = 44 }: { progress: DailyProgress; size?: number }) {
  if (progress.display === 'none') return null;

  if (progress.display === 'level') {
    return (
      <span
        className={`rounded px-2 py-0.5 text-[12px] font-medium ${STATUS_TEXT[progress.status]} bg-kw-bg`}
        title={`Today: ${fmt(progress.value)} vs goal ${fmt(progress.goal)}`}
      >
        {fmt(progress.value)} <span className="text-kw-muted">/ {fmt(progress.goal)}</span>
      </span>
    );
  }

  const fill =
    progress.display === 'attainment' ? Math.min(progress.pct, 100) / 100 : progress.remainingPct / 100;
  const label =
    progress.display === 'attainment'
      ? `${String(Math.round(progress.pct))}%`
      : `${String(Math.round(progress.remainingPct))}%`;
  const title =
    progress.display === 'attainment'
      ? `${fmt(progress.value)} of ${fmt(progress.goal)} today`
      : `${fmt(progress.value)} of a ${fmt(progress.goal)} budget used — ${label} left`;

  const stroke = 4;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;

  return (
    <span
      className={`relative inline-grid place-items-center ${STATUS_TEXT[progress.status]}`}
      style={{ width: size, height: size }}
      role="img"
      aria-label={title}
      title={title}
    >
      <svg width={size} height={size} viewBox={`0 0 ${String(size)} ${String(size)}`} className="-rotate-90">
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke="currentColor"
          strokeOpacity={0.15}
          strokeWidth={stroke}
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke="currentColor"
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={c}
          strokeDashoffset={c * (1 - fill)}
        />
      </svg>
      <span className="absolute text-[10px] font-semibold tabular-nums">{label}</span>
    </span>
  );
}
